import React, { useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';

const DonutChart = ({ value, label, showLabel = true, isFullYear = false, size = 80 }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext('2d');

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const parseNumericValue = (val) => {
      if (val === null || val === undefined || val === "null") return 0;
      if (typeof val === 'string' && val.includes('%')) {
        return parseInt(val.replace('%', '')) || 0;
      }
      return parseInt(val) || 0;
    };

    const numericValue = parseNumericValue(value);

    const CHART_COLORS = {
      fill: '#F4B183',
      background: '#FBE5D6',
      text: getComputedStyle(document.documentElement)
        .getPropertyValue('--foreground').trim() || '#111111',
      mutedText: '#888888',
      primaryBg: '#1e3a5f',
      primaryText: '#ffffff',
    };

    // ✅ Font sizes now scale with the donut's own size, instead of a fixed
    // 12px that turns unreadable on small donuts and looks tiny/lost on big
    // ones. Clamped so it never gets too small or absurdly large.
    const baseFont = Math.round(Math.min(18, Math.max(8, size * 0.13)));
    const smallFont = Math.round(Math.min(14, Math.max(7, size * 0.11)));

    chartRef.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        datasets: [{
          data: [numericValue, 100 - numericValue],
          backgroundColor: [
            CHART_COLORS.fill,
            CHART_COLORS.background
          ],
          borderWidth: 0,
          borderRadius: 0,
          cutout: '68%',
        }]
      },
      options: {
        // ✅ Explicit sizing (not responsive) — reliable inside CSS grid /
        // flex cells that may not report a measurable size to a
        // ResizeObserver right away. Crispness comes from devicePixelRatio
        // below, not from responsive resizing.
        responsive: false,
        maintainAspectRatio: false,
        devicePixelRatio: Math.max(2, window.devicePixelRatio || 1),
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false }
        },
        rotation: -90,
        circumference: 360,
        animation: false,
      },
      plugins: [{
        id: 'centerText',
        beforeDraw: (chart) => {
          const { width, height, ctx } = chart;
          ctx.save();
          ctx.textRendering = 'optimizeLegibility';

          const centerX = width / 2;
          const centerY = height / 2;

          if (isFullYear) {
            // Value badge (top)
            const valueText = String(value || '0%');
            ctx.font = `bold ${baseFont}px sans-serif`;
            const valueWidth = ctx.measureText(valueText).width + Math.round(baseFont * 1.1);
            const valueHeight = Math.round(baseFont * 1.5);
            const valueY = centerY - valueHeight * 0.65;

            ctx.fillStyle = CHART_COLORS.primaryBg;
            ctx.beginPath();
            ctx.roundRect(centerX - valueWidth / 2, valueY - valueHeight / 2, valueWidth, valueHeight, 3);
            ctx.fill();

            ctx.fillStyle = CHART_COLORS.primaryText;
            ctx.textBaseline = 'middle';
            ctx.textAlign = 'center';
            ctx.fillText(valueText, centerX, valueY);

            // Label badge (bottom)
            ctx.font = `bold ${smallFont}px sans-serif`;
            const labelText = label || 'FY';

            const labelWidth = ctx.measureText(labelText).width + Math.round(smallFont * 1.1);
            const labelHeight = Math.round(smallFont * 1.5);
            const labelY = centerY + valueHeight * 0.65;

            ctx.fillStyle = CHART_COLORS.primaryBg;
            ctx.beginPath();
            ctx.roundRect(centerX - labelWidth / 2, labelY - labelHeight / 2, labelWidth, labelHeight, 3);
            ctx.fill();

            ctx.fillStyle = CHART_COLORS.primaryText;
            ctx.textBaseline = 'middle';
            ctx.textAlign = 'center';
            ctx.fillText(labelText, centerX, labelY);

          } else if (showLabel && label) {
            // Value text
            ctx.font = `bold ${baseFont}px sans-serif`;
            ctx.textBaseline = 'middle';
            ctx.textAlign = 'center';
            ctx.fillStyle = CHART_COLORS.text;
            ctx.fillText(value || '0%', centerX, centerY - baseFont * 0.6);

            // Label text
            ctx.font = `bold ${smallFont}px sans-serif`;
            ctx.fillStyle = CHART_COLORS.mutedText;
            ctx.fillText(label, centerX, centerY + baseFont * 0.6);

          } else {
            ctx.font = `bold ${baseFont}px sans-serif`;
            ctx.textBaseline = 'middle';
            ctx.textAlign = 'center';
            ctx.fillStyle = CHART_COLORS.text;
            ctx.fillText(value || '0%', centerX, centerY);
          }

          ctx.restore();
        }
      }]
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [value, label, showLabel, isFullYear, size]);

  return (
    <div style={{ width: `${size}px`, height: `${size}px`, position: 'relative' }}>
      <canvas ref={canvasRef} width={size} height={size} style={{ width: `${size}px`, height: `${size}px` }} />
    </div>
  );
};

export default DonutChart;